import React, { useLayoutEffect, useRef, useState } from 'react';
import { components } from 'react-select';
import Avatar from 'src/componentes/avatar';
import HoverCard from 'src/componentes/hover-card';
import guid from 'src/utils/guid';

const badgeStyle = {
  flex: '0 0 auto',
  padding: '2px 6px',
  marginRight: 4,
  borderRadius: 2,
  backgroundColor: '#e6e8ea',
  fontSize: 12,
  lineHeight: '20px',
  cursor: 'help',
};

export const compactMultiStyles = {
  valueContainer: (base) => ({
    ...base,
    minWidth: 0,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    position: 'relative',
  }),
  multiValue: (base) => ({
    ...base,
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 1,
  }),
  multiValueLabel: (base) => ({
    ...base,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
};

const CompactMultiValueContainer = (props) => {
  const selected = props.getValue();
  const { compactMultiShowAvatar = false } = props.selectProps;
  const shouldShowAvatar = (item) =>
    typeof compactMultiShowAvatar === 'function'
      ? compactMultiShowAvatar(item)
      : compactMultiShowAvatar;
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const badgeRef = useRef(null);
  const chipRefs = useRef([]);
  const badgeId = useRef(`compact-multi-${guid()}`);
  const [visibleCount, setVisibleCount] = useState(selected.length);
  const [values, input] = props.children;
  const chips = Array.isArray(values) ? values : [values];

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || selected.length <= 1) return;

    const updateVisibleCount = () => {
      const styles = window.getComputedStyle(container);
      const availableWidth =
        container.clientWidth -
        parseFloat(styles.paddingLeft) -
        parseFloat(styles.paddingRight) -
        12;
      const chipWidths = selected.map(
        (_, index) =>
          chipRefs.current[index]?.getBoundingClientRect().width || 0
      );
      const badgeWidth = badgeRef.current.getBoundingClientRect().width + 4;
      let width = 0;
      let nextVisibleCount = 1;

      for (let index = 0; index < selected.length; index += 1) {
        width += chipWidths[index];
        const hasHiddenItems = index < selected.length - 1;
        if (
          index > 0 &&
          width + (hasHiddenItems ? badgeWidth : 0) > availableWidth
        ) {
          break;
        }
        nextVisibleCount = index + 1;
      }

      setVisibleCount((current) =>
        current === nextVisibleCount ? current : nextVisibleCount
      );
    };

    updateVisibleCount();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateVisibleCount);
      return () => window.removeEventListener('resize', updateVisibleCount);
    }

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(container);
    observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [selected]);

  if (selected.length <= 1) {
    return <components.ValueContainer {...props} />;
  }

  const count = Math.min(visibleCount, selected.length);
  const hiddenItems = selected.slice(count);
  const hiddenLabels = hiddenItems
    .map((item) => props.selectProps.getOptionLabel(item))
    .join(', ');

  return (
    <components.ValueContainer
      {...props}
      innerProps={{ ...props.innerProps, ref: containerRef }}
    >
      {chips.slice(0, count)}
      {count < selected.length && (
        <HoverCard
          className="tw:rounded-lg tw:p-0 tw:shadow-[0_8px_24px_rgba(30,45,60,0.16)]"
          content={
            <div className="tw:max-h-60 tw:max-w-75 tw:min-w-40 tw:overflow-y-auto">
              {hiddenItems.map((item, index) => (
                <div
                  key={props.selectProps.getOptionValue(item)}
                  className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:not-last:[border-bottom:1px_solid_#edf0f3]"
                >
                  {shouldShowAvatar(item) ? (
                    <Avatar
                      id={`${badgeId.current}-avatar-${index}`}
                      foto={item.fotoArquivo}
                      label={props.selectProps.getOptionLabel(item)}
                      showLabel={false}
                      showTooltip={false}
                      size={24}
                      border={0}
                      semMargin
                    />
                  ) : (
                    <span className="tw:size-1.75 tw:flex-none tw:rounded-full tw:bg-primary" />
                  )}
                  <span className="tw:text-xs tw:leading-4.5 tw:wrap-anywhere tw:text-[#263746]">
                    {props.selectProps.getOptionLabel(item)}
                  </span>
                </div>
              ))}
            </div>
          }
        >
          <span aria-label={hiddenLabels} tabIndex={0} style={badgeStyle}>
            +{selected.length - count}
          </span>
        </HoverCard>
      )}
      {input}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          display: 'flex',
          width: 'max-content',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {chips.map((chip, index) => (
          <span
            key={index}
            ref={(element) => {
              chipRefs.current[index] = element;
            }}
            style={{ display: 'inline-flex' }}
          >
            {chip}
          </span>
        ))}
        <span ref={badgeRef} style={badgeStyle}>
          +{selected.length}
        </span>
      </div>
    </components.ValueContainer>
  );
};

export default CompactMultiValueContainer;
